<script lang="ts">
  import { createEventDispatcher } from 'svelte';

  import Button from '@/comps-sdk/components/button';
  import Cascade, { type IOptions } from '@/comps-sdk/components/cascade';
  import Dialog from '@/comps-sdk/components/dialog-v';
  import Field from '@/comps-sdk/components/field';
  import Loading from '@/comps-sdk/components/loading-v';
  import Picker from '@/comps-sdk/components/picker';
  import Popup from '@/comps-sdk/components/popup';
  import { getCodeMap, getOrderDetail, registerUserAddrInfo } from '@/comps-sdk/components/receive-result/services';
  import { toast as Toast } from '@/comps-sdk/components/toast';
  import { getActivityConfig } from '@/comps-sdk/modules/act-utils';
  import bridge from '@/comps-sdk/modules/bridge';

  import { getAreaOptions, infoValidator, validator } from './utils';

  export let receiveResult: any = {};

  let init = false;
  let isInitSucc = true;
  let isModifying = true;
  let orderIdList: any[] = [];
  let userType = -1;
  let isShowArea = false;
  let isShowCountryCode = false;
  let name = '';
  let phoneNumber = '';
  let phoneNumberPrefix = '+86';
  let address = '';
  let door = '';
  let cascadeValue = '';
  let options: IOptions[] = [];
  let countryList: any[] = [];
  let contactInfo: any = null;
  let addressForm = {
    name: '',
    tel: '',
    addr: '',
  };
  const addressFormRequiredFields = ['name', 'tel', 'addr'];
  const addressFormExtendFields: {
    name: keyof typeof addressForm;
    desc: string;
  }[] = [];
  let orderDetail: any = null;
  const pcaStorageKey = 'common_receive_good_pca';
  const pcaVersion = '1.0';
  const dispatch = createEventDispatcher();

  $: showResult = receiveResult?.showResult;
  $: successMode = receiveResult?.code === 0;
  $: errorMsg = receiveResult?.msg ?? '领取失败了，请稍后再试～';
  $: btnStatus = isModifying && !!contactInfo;
  $: btnText = successMode && btnStatus ? '修改' : '确认';
  $: phoneNumberPrefixColumns = countryList.reduce((acc, cur) => {
    const { c_area_text: cAreaText } = cur;
    return [...acc, cAreaText];
  }, []);

  // 获取订单详情
  const getOrderDetailFn = async () => {
    if (orderIdList.length && userType) {
      const orderRes = await getOrderDetail({
        orderId: orderIdList[0],
        userType,
      });
      const { detail = null } = orderRes as any;
      orderDetail = detail;
    }
  };

  const getUserAddressInfo = () => {
    const {
      propertyData: [{ propertyDetail }],
    } = receiveResult;
    const addrStr = orderDetail?.user_addr_info || '';
    const addressFormCfg = propertyDetail?.physical_addr_cfg || [];
    if (!addrStr) {
      return null;
    }
    const cInfo: { [key: string]: string } = {};
    const addrInfo = JSON.parse(addrStr);
    addrInfo.forEach(({ name, desc }: { name: string; desc: string }) => {
      // 确保回显读取的字段存在于当前配置之中
      if (addressFormCfg.findIndex((item: any) => item.name === name) > -1) {
        cInfo[name] = desc;
      }
    });
    contactInfo = Object.keys(cInfo).length ? cInfo : null;
  };

  const initAddressForm = async () => {
    try {
      init = false;
      const {
        propertyData: [{ propertyDetail }],
        orderId = [],
      } = receiveResult;
      orderIdList = orderId;
      if (!orderIdList.length) {
        console.error('实物弹窗未传入orderId');
        init = true;
        return;
      }
      // get user type
      const activityConfig = await getActivityConfig();
      userType = Number(activityConfig.main_account_type) - 1;

      await getOrderDetailFn();
      const addressFormCfg = propertyDetail.physical_addr_cfg || [];
      addressFormCfg.forEach((cfgItem: any) => {
        const { name = '' } = cfgItem;
        if (!name || addressFormRequiredFields.indexOf(name) > -1) {
          return;
        }
        // 遇到重复的直接覆盖
        addressForm = {
          ...addressForm,
          name: '',
        };
        const idx = addressFormExtendFields.findIndex(item => item.name === name);
        if (idx > -1) {
          addressFormExtendFields.splice(idx, 1);
        }
        addressFormExtendFields.push(cfgItem);
      });

      getUserAddressInfo();
      isInitSucc = true;
    } catch (e) {
      console.log(e);
      isInitSucc = false;
    }
    init = true;
  };

  const initAreaOptions = async () => {
    const res = await bridge.getNativeStorage(pcaStorageKey);
    // 命中storage数据
    if (res) {
      try {
        const { pca = [], version = '' } = JSON.parse(res);
        if (version === pcaVersion && pca.length) {
          options = pca;
          return;
        }
      } catch (e) {
        console.error('pca localStorage数据解析失败', e);
      }
    }
    // 非命中需要拉取
    const pcaData = await getAreaOptions();
    options = pcaData;
    if (pcaData.length) {
      bridge.setNativeStorage(
        pcaStorageKey,
        JSON.stringify({
          version: pcaVersion,
          pca: pcaData,
        }),
      );
    }
  };
  // 获取区号
  const handleGetCountryCode = async () => {
    try {
      const result = await getCodeMap();
      countryList = result.data.data;
    } catch (error) {
      console.error('Error in getCodeMap', error);
    }
  };

  const handleConfirmCountryCode = (value: any, index: number) => {
    phoneNumberPrefix = countryList[index].c_area_code;
    isShowCountryCode = false;
  };

  // 选择地址结束回调函数
  const handleFinishSelectAddress = (param: any) => {
    const { selectedOptions = [] } = param.detail;
    isShowArea = false;
    address = selectedOptions.map((option: { text: string }) => option.text).join('/');
  };

  // 地址表单项非空验证
  const handleAddressValidate = () => {
    if (!name || !phoneNumber || !address || !door) {
      Toast('请填写完整信息~');
      return false;
    }
    return true;
  };

  // 提交地址信息，返回是否成功
  const handleRegisterAddr = async () => {
    // 校验输入电话的合法性
    const validRet = await infoValidator({
      formValue: {
        tel: phoneNumber,
      },
      areaCode: phoneNumberPrefix,
    });
    if (!validRet.valid) {
      Toast('电话号码格式不合法!');

      return false;
    }

    const addrData = {
      ...addressForm,
      name,
      addr: `${address}${door}`,
      tel: `${phoneNumberPrefix} ${phoneNumber}`,
    };
    try {
      await Promise.all(orderIdList.map(orderId => registerUserAddrInfo({
        orderId,
        userType,
        addrInfo: addrData as any, // 遗留问题，结构不匹配，未作修改
      })));
      Toast('您已成功录入地址~');
      return true;
    } catch (e) {
      Toast('保存信息失败，请重试');
      return false;
    }
  };

  const handleBtnClick = async () => {
    if (!successMode) {
      dispatch('close');
      return;
    }

    // 去填写地址
    if (btnStatus) {
      isModifying = !isModifying;
      return;
    }
    if (handleAddressValidate()) {
      Dialog.confirm({
        message: '提交后将无法修改，请检查填写信息无误后进行确认',
      }).then(async () => {
        const isSuccess = await handleRegisterAddr();
        if (isSuccess) {
          dispatch('close');
        }
      });
    }
  };

  // 初始化时运行
  initAddressForm();
  initAreaOptions();
  handleGetCountryCode();
</script>

<div class="result-panel">
  <div class="result-header">
    {showResult.title}
  </div>
  <div class="result-content" class:result-content-failed={!successMode}>
    <div class="result-desc">
      {successMode ? showResult.desc : errorMsg}
    </div>
    {#if showResult.img}
      <div class="result-img-wrapper">
        <img class="result-img" src={showResult.img} alt="" />
      </div>
    {/if}
  </div>
  {#if !init && successMode}
    <Loading class="result-panel-loading" size="24px">信息加载中...</Loading>
  {/if}
  {#if init && successMode && orderDetail}
    <div class="address-form-container">
      {#if btnStatus}
        <div class="address-form">
          <Field bind:value={contactInfo.name} label="姓名" disabled />
          <Field bind:value={contactInfo.tel} label="电话" disabled />
          <Field bind:value={contactInfo.addr} label="地址" disabled />
          {#each addressFormExtendFields as item (item.name)}
            <Field bind:value={contactInfo[item.name]} label={item.desc} disabled />
          {/each}
        </div>
      {:else}
        <div class="address-form">
          <Field bind:value={name} placeholder="请输入姓名" rules={[{ validator, message: '' }]} />
          <section class="phone-container">
            <Field
              bind:value={phoneNumberPrefix}
              class="phone-prefix"
              readonly
              on:click={() => {
                isShowCountryCode = true;
              }}
            />
            <Field
              bind:value={phoneNumber}
              class="phone-content"
              placeholder="请输入电话"
              rules={[{ validator, message: '' }]}
            />
          </section>
          <Field
            bind:value={address}
            readonly
            placeholder="请选择所在地区"
            rules={[{ validator, message: '' }]}
            on:click={() => {
              isShowArea = true;
            }}
          />
          <Field bind:value={door} placeholder="请输入详细地址" rules={[{ validator, message: '' }]} />
          {#each addressFormExtendFields as item (item.name)}
            {#if item.name}
              <Field bind:value={addressForm[item.name]} placeholder={`请输入${item.desc}`} />
            {/if}
          {/each}
        </div>
      {/if}
    </div>
  {/if}
  {#if init && successMode && !isInitSucc}
    <div class="load-error-container">
      <p>实物地址填写信息加载失败，请尝试重新加载～</p>
    </div>
  {/if}
  {#if init && successMode && !isInitSucc}
    <div class="btn-wrapper">
      <Button disabled={!init} on:click={initAddressForm}>重试</Button>
    </div>
  {/if}
  {#if !btnStatus && init && isInitSucc}
    <div class="btn-wrapper">
      <Button disabled={!init} on:click={handleBtnClick}>
        {btnText}
      </Button>
    </div>
  {/if}

  <Popup bind:value={isShowCountryCode} round position="bottom" getContainer="body">
    <Picker
      showToolbar
      title="选择区号"
      bind:columns={phoneNumberPrefixColumns}
      on:cancel={() => {
        isShowCountryCode = false;
      }}
      on:confirm={(event) => {
        const { detail } = event;
        handleConfirmCountryCode(detail.value, detail.index);
      }}
    />
  </Popup>
  <Popup bind:value={isShowArea} round position="bottom" getContainer="body">
    <Cascade
      bind:value={cascadeValue}
      title="请选择所在地区"
      {options}
      on:close={() => {
        isShowArea = false;
      }}
      on:finish={handleFinishSelectAddress}
    />
  </Popup>
</div>

<style lang="scss">
  .result-panel {
    padding: 0 15px;
    display: flex;
    flex-direction: column;
    :global(.result-panel-loading) {
      align-self: center;
      flex: 1;
    }
    .result-header {
      margin-top: 15px;
      text-align: center;
      font-family: PingFangSC-Medium;
      font-size: 18px;
      color: #191919;
    }
    .result-content {
      margin: 12px 0;
      text-align: center;
      &.result-content-failed {
        flex: 1;
      }
      .result-desc {
        padding: 0 15px;
        font-family: PingFangSC-Regular;
        font-size: 16px;
        color: #191919;
      }
      .result-img-wrapper {
        margin-top: 24px;
        .result-img {
          width: auto;
          max-width: 100%;
          max-height: 140px;
          border-radius: 6px;
        }
      }
    }
    .load-error-container {
      flex: 1;
      width: 100%;
      margin-bottom: 24px;
      display: flex;
      justify-content: center;
      align-items: center;
      padding: 12px 0;
    }
    .address-form-container {
      flex: 1;
      width: 100%;
      margin-bottom: 24px;
      .address-form {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        .phone-container {
          width: calc(100% - 32px);
          display: flex;
          position: relative;
          :global(.phone-prefix) {
            width: 20%;
            padding: 10px 0;
          }
          :global(.phone-content) {
            width: 80%;
            padding: 10px 0;
          }
          &::after {
            position: absolute;
            box-sizing: border-box;
            content: ' ';
            pointer-events: none;
            right: 0;
            bottom: 0;
            left: 0;
            border-bottom: 1px solid #ebedf0;
            -webkit-transform: scaleY(0.5);
            transform: scaleY(0.5);
          }
        }
      }
    }
    .btn-wrapper {
      margin: 10px 0;
      text-align: center;
    }
  }
</style>
